/**
 * AccessLabReportPrint.tsx
 *
 * The printed report of an Access Labs result (plan 2026-10-16-001, U14,
 * "A readable report built from the values"): a header with the patient, the
 * ordering provider, the location, the collected and received dates and the
 * status, then the values with their flags in words, then the notes. A
 * preliminary says it is not final.
 *
 * Printing follows the app's existing print views (PatientDetailPage's
 * appointment and payment histories, SchedulePage's packets): a window opened
 * synchronously in the click handler, its own document with its own plain CSS,
 * then print(). The app's chrome is never in that document, so nothing has to
 * be hidden, and no Tailwind class is used here at all - a Tailwind max-*
 * variant would fire at a printed page's width (ui/CLAUDE.md). The report is a
 * React component rendered into that window, so it can be tested directly.
 */
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import type { AccessLabResultDetail } from '@/services/lab-orders.service';
import {
  accessFlagText,
  accessReceivedAt,
  accessStatusLabel,
  formatAccessBirthDate,
  formatAccessDateTime,
  isPreliminary,
  sectionsWithValues,
} from './accessLabFormat';

const PRINT_CSS = `
  body { font-family: Arial, Helvetica, sans-serif; margin: 24px; color: #1e293b; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .not-final { font-size: 14px; font-weight: 700; margin: 0 0 12px; padding: 6px 8px; border: 2px solid #1e293b; display: inline-block; }
  dl.header { display: grid; grid-template-columns: max-content 1fr max-content 1fr; gap: 4px 12px; font-size: 12px; margin: 0 0 16px; }
  dl.header dt { font-weight: 600; color: #475569; }
  dl.header dd { margin: 0; }
  h2 { font-size: 14px; margin: 16px 0 6px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; border-bottom: 2px solid #334155; padding: 6px 8px; font-weight: 600; }
  td { border-bottom: 1px solid #e2e8f0; padding: 5px 8px; vertical-align: top; }
  tr.abnormal td { font-weight: 700; }
  .obs-note { font-size: 11px; color: #475569; font-weight: 400; margin-top: 2px; }
  ul.notes { font-size: 12px; padding-left: 18px; }
  .empty { font-size: 13px; color: #64748b; }
  @media print { body { margin: 0; } thead { display: table-header-group; } tr { break-inside: avoid; } }
`;

interface AccessLabReportPrintProps {
  /** The version being viewed, as the result view loaded it. */
  detail: AccessLabResultDetail;
}

export function AccessLabReportPrint({ detail }: AccessLabReportPrintProps) {
  const status = accessStatusLabel(detail.status, detail.statusLabel);
  const received = accessReceivedAt(detail);
  const provider = detail.summary.providerName
    ? `${detail.summary.providerName}${detail.summary.providerNpi ? ` (NPI ${detail.summary.providerNpi})` : ''}`
    : '—';
  const sections = sectionsWithValues(detail);
  const hasValues = sections.length > 0;

  return (
    <div className="report">
      <h1>{detail.sourceName || 'Access Labs'} result{detail.orderNumber ? ` – Accession #${detail.orderNumber}` : ''}</h1>
      {isPreliminary(detail.status, detail.statusLabel) && <p className="not-final">Preliminary – not final</p>}
      <dl className="header" data-testid="access-print-header">
        <dt>Patient</dt><dd>{detail.patientName}</dd>
        <dt>Date of birth</dt><dd>{formatAccessBirthDate(detail.birthDate)}</dd>
        <dt>Ordering provider</dt><dd>{provider}</dd>
        <dt>Location</dt><dd>{detail.summary.location ?? '—'}</dd>
        <dt>Collected</dt><dd>{formatAccessDateTime(detail.summary.collectedAt)}</dd>
        <dt>Received</dt><dd>{formatAccessDateTime(received)}</dd>
        <dt>Status</dt><dd>{status ?? '—'}</dd>
      </dl>

      {hasValues ? (
        sections.map((section) => (
          <section key={section.id}>
            <h2>{section.title}</h2>
            <table>
              <thead>
                <tr><th>Test</th><th>Result</th><th>Unit</th><th>Reference range</th><th>Flag</th></tr>
              </thead>
              <tbody>
                {section.observations.map((obs, i) => {
                  const flag = accessFlagText(obs.flag, obs.abnormal);
                  return (
                    <tr key={`${obs.code}-${i}`} className={flag ? 'abnormal' : undefined}>
                      <td>
                        {obs.label || obs.code}
                        {obs.notes.map((n, ni) => <div key={ni} className="obs-note">{n}</div>)}
                      </td>
                      <td>{obs.value}</td>
                      <td>{obs.unit ?? ''}</td>
                      <td>{obs.referenceRange ?? ''}</td>
                      <td>{flag ?? ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        ))
      ) : (
        <p className="empty">No values came with this result.</p>
      )}

      {detail.notes.length > 0 && (
        <section>
          <h2>Notes</h2>
          <ul className="notes">
            {detail.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * Open the report in its own window and print it. Call it straight from the
 * click handler: a window opened after an await is blocked as a pop-up.
 * Returns false when the browser blocked the window.
 */
export function printAccessLabReport(detail: AccessLabResultDetail): boolean {
  const win = window.open('', '_blank');
  if (!win) return false;

  const doc = win.document;
  doc.title = `Access Labs result${detail.orderNumber ? ` #${detail.orderNumber}` : ''}`;
  const style = doc.createElement('style');
  style.textContent = PRINT_CSS;
  doc.head.appendChild(style);
  const mount = doc.createElement('div');
  doc.body.appendChild(mount);

  // Rendered synchronously so the report is on the page before print() runs.
  const root = createRoot(mount);
  flushSync(() => root.render(<AccessLabReportPrint detail={detail} />));

  win.focus();
  win.print();
  return true;
}
